import { atom } from 'jotai';
import localforage from 'localforage';

export const storage = localforage.createInstance({
	name: 'focus-resa',
});

export const atomWithForage = <T extends object | string | number>(
	key: string,
	initialValue: T,
) => {
	const baseAtom = atom(initialValue);
	baseAtom.onMount = (setValue) => {
		(async () => {
			const item = await storage.getItem<T>(key);
			item && setValue(item);
		})();
	};
	return atom(
		(get) => get(baseAtom),
		(get, set, update: T | ((x: T) => T)) => {
			const nextValue =
				typeof update === 'function' ? update(get(baseAtom)) : update;
			set(baseAtom, nextValue);
			storage.setItem(key, nextValue);
		},
	);
};

export const atomWithStaleRevalidate = <T extends object | string | number>(
	storageKey: string,
	refresh: (lastUpdate?: number) => Promise<T | false>,
	merge: (current: T, next: T) => T,
	initialValue: T,
	writeOnLocalChange = false,
) => {
	const baseAtom = atom(initialValue);
	baseAtom.onMount = (setValue) => {
		const t = Date.now(); // mark time before call to avoid missing updates in the interim
		(async () => {
			const item = await storage.getItem<T>(storageKey);
			item && setValue(item);
			const lastUpdate = await storage.getItem<number>(`${storageKey}_t`);
			const fresh = await refresh(lastUpdate ?? undefined);
			if (fresh) {
				setValue((current: T) => {
					const uptd = merge(current, fresh);
					storage.setItem(storageKey, uptd);
					return uptd;
				});
				storage.setItem(`${storageKey}_t`, t);
			}
		})();
	};
	return atom(
		(get) => get(baseAtom),
		(get, set, update: T | ((x: T) => T)) => {
			const nextValue =
				typeof update === 'function' ? update(get(baseAtom)) : update;
			set(baseAtom, nextValue);
			if (writeOnLocalChange) {
				storage.setItem(storageKey, nextValue);
				storage.setItem(`${storageKey}_t`, Date.now());
			}
		},
	);
};
